import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { DocumentFlowButtonComponent } from 'app/modules/shared/document-flow-button/document-flow-button.component';
import OrderAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity';
import { DocumentTypeEnum } from 'app/common/enums/document-types.enum';

/**
 * Action event types
 */
export type OrderAction =
  | 'synchronize'
  | 'move'
  | 'disunify'
  | 'release'
  | 'viewLogs'
  | 'reprintInvoice';

/**
 * Order Actions Cell Renderer Component
 *
 * Renders a 3-dot menu button with action items for each order row.
 * Available actions depend on the order's state and permissions.
 *
 * Actions include:
 * - Sincronizar (Synchronize)
 * - Mover (Move) - only if canEdit and no externalKeys
 * - Desunificar (Disunify) - only if operationType === 4
 * - Liberar (Release) - only if canRelease permission and order.canRelease
 * - Visualizar logs (View logs)
 * - Reimprimir factura (Reprint invoice) - conditional
 *
 * Copied from: src/app/modules/components/monitor/table-load-list/table-load-list.component.html (lines 368-430)
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   colId: 'actions',
 *   cellRenderer: OrderActionsCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: "app-order-actions-cell-renderer",
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    DocumentFlowButtonComponent,
  ],
  template: `
    <div class="flex items-center">
      <button
        mat-icon-button
        [matMenuTriggerFor]="actionMenu"
        matTooltip="Acciones"
      >
        <mat-icon>more_vert</mat-icon>
      </button>

      <mat-menu #actionMenu="matMenu">
        <!-- Sincronizar -->
        <button
          [matTooltip]="
            hasMoreThanOneSelected
              ? 'No se puede sincronizar al tener mas de 1 documento seleccionado, utilizar la sincronización masiva en la parte superior derecha'
              : !onlySelectedIsCurrent && hasSelected
                ? 'Por favor deseleccione el documento ' +
                  (firstItemSelected?.serial ??
                    firstItemSelected?.serials ??
                    '') +
                  ' primero antes de sincronizar'
                : ''
          "
          mat-menu-item
          (click)="onAction('synchronize')"
          [disabled]="
            !canSynchronize ||
            hasMoreThanOneSelected ||
            (!onlySelectedIsCurrent && hasSelected)
          "
        >
          <mat-icon svgIcon="heroicons_outline:arrow-path"></mat-icon>
          <span>Sincronizar</span>
        </button>

        <!-- Mover -->
        <button
          [matTooltip]="
            hasMoreThanOneSelected
              ? 'No se puede mover al tener mas de 1 documento seleccionado, utilizar el mover masivo en la parte superior derecha'
              : !onlySelectedIsCurrent && hasSelected
                ? 'Por favor deseleccione el documento ' +
                  (firstItemSelected?.serial ??
                    firstItemSelected?.serials ??
                    '') +
                  ' primero antes de mover'
                : ''
          "
          [disabled]="
            hasMoreThanOneSelected || (!onlySelectedIsCurrent && hasSelected)
          "
          *ngIf="canEdit && !hasExternalKeys"
          mat-menu-item
          (click)="onAction('move')"
        >
          <mat-icon svgIcon="heroicons_outline:pencil-square"></mat-icon>
          <span>Mover</span>
        </button>

        <!-- Desunificar -->
        <button
          *ngIf="isUnifiedOperation"
          [matTooltip]="
            hasMoreThanOneSelected
              ? 'No se puede desunificar al tener mas de 1 documento seleccionado, utilizar la desunificación masiva en la parte superior derecha'
              : !onlySelectedIsCurrent && hasSelected
                ? 'Por favor deseleccione el documento ' +
                  (firstItemSelected?.serial ??
                    firstItemSelected?.serials ??
                    '') +
                  ' primero antes de desunificar'
                : ''
          "
          mat-menu-item
          (click)="onAction('disunify')"
          [disabled]="
            !canSynchronize ||
            hasMoreThanOneSelected ||
            (!onlySelectedIsCurrent && hasSelected)
          "
        >
          <mat-icon svgIcon="heroicons_outline:document-duplicate"></mat-icon>
          <span>Desunificar</span>
        </button>

        <!-- Liberar -->
        <button
          [matTooltip]="
            hasMoreThanOneSelected
              ? 'No se puede liberar al tener mas de 1 documento seleccionado, utilizar la liberación masiva en la parte superior derecha'
              : !onlySelectedIsCurrent && hasSelected
                ? 'Por favor deseleccione el documento ' +
                  (firstItemSelected?.serial ??
                    firstItemSelected?.serials ??
                    '') +
                  ' primero antes de liberar'
                : ''
          "
          *ngIf="canRelease"
          mat-menu-item
          (click)="onAction('release')"
          [disabled]="!orderCanRelease || hasMoreThanOneSelected"
        >
          <mat-icon svgIcon="heroicons_outline:lock-open"></mat-icon>
          <span>Liberar</span>
        </button>

        <!-- Visualizar logs -->
        <button mat-menu-item (click)="onAction('viewLogs')">
          <mat-icon>visibility</mat-icon>
          <span>Visualizar logs</span>
        </button>

        <!-- Reimprimir factura -->
        <button
          *ngIf="order?.type?.code === documentTypeEnum.FACTURA"
          mat-menu-item
          (click)="onAction('reprintInvoice')"
          [disabled]="!canReprintInvoice"
          matTooltip="Reimprimir"
        >
          <mat-icon>print</mat-icon>
          <span>Reimprimir</span>
        </button>

        <!-- Document Flow Button -->
        <app-document-flow-button [element]="order"></app-document-flow-button>
      </mat-menu>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderActionsCellRendererComponent
  implements CellRenderer<any, any>
{
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, any>;

  /**
   * Event emitted when an action is clicked
   * Parent component should handle these actions
   */
  @Output() actionClick = new EventEmitter<{ action: OrderAction; row: any }>();

  /**
   * Can edit permission (from cellRendererParams)
   */
  get canEdit(): boolean {
    return this.params?.canEdit ?? false;
  }

  /**
   * Can release permission (from cellRendererParams)
   */
  get canRelease(): boolean {
    return this.params?.canRelease ?? false;
  }

  /**
   * Order data
   */
  get order(): any {
    return this.params?.data;
  }

  /**
   * Can synchronize this order
   * Reads pre-calculated property from parent component's validateRequirements()
   */
  get canSynchronize(): boolean {
    return this.order?.canSynchronize ?? false;
  }

  /**
   * Order has external keys
   */
  get hasExternalKeys(): boolean {
    return !!this.order?.externalKeys;
  }

  /**
   * Is this a unified operation (operationType === 4)
   */
  get isUnifiedOperation(): boolean {
    return this.order?.operationType === 4;
  }

  /**
   * Can this specific order be released
   */
  get orderCanRelease(): boolean {
    return this.order?.canRelease ?? false;
  }

  /**
   * Can reprint invoice for this order
   * Reads pre-calculated property from parent component's validateRequirements()
   */
  get canReprintInvoice(): boolean {
    return this.order?.canReprintInvoice ?? false;
  }

  /**
   * Handle action click
   * Calls the callback function from cellRendererParams if available
   */
  onAction(action: OrderAction): void {
    // Call the callback from params (spread by getCellRendererParams)
    const onAction = this.params?.onAction;
    if (onAction && typeof onAction === "function") {
      onAction(action, this.order);
    } else {
      // Fallback to emitting event (for backward compatibility)
      this.actionClick.emit({ action, row: this.order });
    }
  }

  get selectedCount(): number {
    const getSelectedCount = this.params?.selectedCount;
    if (getSelectedCount && typeof getSelectedCount === "function") {
      return getSelectedCount();
    }
    return 0;
  }

  get hasMoreThanOneSelected(): boolean {
    return this.selectedCount > 1;
  }

  get onlySelectedIsCurrent(): boolean {
    let firstItemSelected = this.params?.firstItemSelected();
    if (typeof firstItemSelected === "function") {
      firstItemSelected = firstItemSelected();
    }

    return (
      this.selectedCount === 1 &&
      firstItemSelected?._id === this?.order?._id &&
      this.selectedCount > 0
    );
  }

  get hasSelected(): boolean {
    return this.selectedCount > 0;
  }

  get firstItemSelected() {
    return this.params?.firstItemSelected();
  }

  documentTypeEnum = DocumentTypeEnum;
}
